import { useEffect, useState } from 'react';
import { listCompanies, listCrypto, listMoods } from '@/content/tickers';
import { priceAt } from '@/engine/prices';
import { priceFmt } from '@/lib/format';
import { useXClone } from '@/state/xclone';
import { useSettings } from '@/state/settings';
import { generateProceduralHeadline } from '@/engine/headlines';

export function TVTickerStrip() {
  const [now, setNow] = useState(() => Date.now());
  const allPosts = useXClone((s) => s.posts);
  const recentXPosts = allPosts.slice(0, 5);
  const playerHandle = useSettings((s) => s.playerHandle);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const tickers = [...listCompanies(), ...listCrypto(), ...listMoods()];

  // Pick rotating headlines: blend recent X-Clone posts (real "news") with
  // procedural Mad-Libs headlines so the ticker stays fresh.
  const idx = Math.floor(now / 8000);
  const useReal = recentXPosts.length >= 2 && idx % 2 === 0;
  const realPost = useReal ? recentXPosts[idx % recentXPosts.length] : null;
  const procHeadline = generateProceduralHeadline(`tv:${idx}`)?.text ?? '';
  const headlineText = realPost
    ? `@${realPost.authorIsSelf ? playerHandle : realPost.authorHandle}: ${realPost.text}`
    : procHeadline;

  return (
    <div className="flex items-center gap-0 overflow-hidden border-t border-[var(--border-subtle)] bg-black py-1">
      <span className="shrink-0 bg-[var(--critical)] px-2 py-0.5 text-mono-xs font-bold text-white">
        LIVE
      </span>
      <div className="flex animate-[ticker_60s_linear_infinite] whitespace-nowrap text-mono-xs text-[var(--text-secondary)] tabular-nums">
        {[...Array(2)].flatMap((_, dup) =>
          tickers.map((c) => {
            const p = priceAt(c, now);
            // 24h % change, not vs 2026-01-01 baseline
            const ref = priceAt(c, now - 24 * 60 * 60 * 1000);
            const change = ref > 0 ? p / ref - 1 : 0;
            const positive = change >= 0;
            return (
              <span key={`${dup}-${c.id}`} className="mx-4 inline-flex items-center gap-1.5">
                <span className="text-[var(--text-primary)]">{c.id}</span>
                <span>{priceFmt(p)}</span>
                <span className={positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]'}>
                  {positive ? '▲' : '▼'} {(change * 100).toFixed(2)}%
                </span>
              </span>
            );
          }),
        )}
        <span className="mx-8 text-[var(--text-muted)]">
          ·  {headlineText}  ·
        </span>
      </div>
      <style>{`
        @keyframes ticker {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
